import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@repo/ui';

interface WarningsIndicatorProps {
  warnings: string[];
}

export const WarningsIndicator: React.FC<WarningsIndicatorProps> = ({ warnings }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!isHovered || !triggerRef.current) {
      setCoords(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  }, [isHovered]);

  if (warnings.length === 0) return null;

  return (
    <>
      <span
        ref={triggerRef}
        className="relative inline-block"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Icon
          name="HiExclamation"
          className="h-4 w-4 text-amber-500"
          aria-label="Transaction has warnings"
        />
      </span>
      {isHovered &&
        coords &&
        createPortal(
          <div
            className="fixed -translate-y-1/2 z-[100] w-72 bg-amber-50 dark:bg-amber-900/95 border border-amber-200 dark:border-amber-700 rounded-lg shadow-xl p-3 pointer-events-none"
            style={{ top: coords.top, left: coords.left }}
            role="tooltip"
          >
            <div className="text-xs text-left">
              <div className="font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center space-x-1">
                <Icon name="HiExclamation" className="h-4 w-4" />
                <span>Warnings</span>
              </div>
              <ul className="space-y-1.5 text-amber-700 dark:text-amber-300">
                {warnings.map((warning, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <span className="text-amber-600 dark:text-amber-400">•</span>
                    <span>{warning}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
